import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';
import api from '../utils/api';

const formatDateForInput = (value) => {
  if (!value) return '';
  if (value instanceof Date) return value.toISOString().split('T')[0];
  if (typeof value === 'string') return value.split('T')[0];
  return '';
};

function WeddingExtraDetails({ _embedded }) {
  const [formData, setFormData] = useState({
    proposalDate: '',
    newSurname: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [detailsRes, partnersRes] = await Promise.all([
        api.get('/api/wedding-details'),
        api.get('/api/wedding-details/partners')
      ]);
      const details = detailsRes.data;
      const partners = partnersRes.data;
      const dates = details?.dates || {};
      const extraDetails = details?.extraDetails || {};
      // Use wedding-level, or migrate from per-partner
      if (dates.proposalDate || extraDetails.newSurname) {
        setFormData({
          proposalDate: formatDateForInput(dates.proposalDate),
          newSurname: extraDetails.newSurname || ''
        });
      } else {
        const p1 = partners?.partnerOne || {};
        const p2 = partners?.partnerTwo || {};
        setFormData({
          proposalDate: formatDateForInput(p1.proposalDate || p2.proposalDate),
          newSurname: p1.newSurname || p2.newSurname || ''
        });
      }
    } catch (err) {
      setError('Failed to load extra details');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError('');
      const updates = {
        extraDetails: { newSurname: formData.newSurname }
      };
      if (formData.proposalDate) {
        updates.dates = { proposalDate: new Date(formData.proposalDate) };
      } else {
        updates.dates = { proposalDate: null };
      }
      await api.post('/api/wedding-details', updates);
      setSuccess('Extra details saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to save extra details');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, px: 2 }}>
        Extra Details
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, px: 2 }}>
        Share additional timeline and personal details that help suppliers understand your story and timeline better.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Card sx={{ bgcolor: 'background.paper', boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Proposal Date"
                value={formData.proposalDate}
                onChange={(e) => setFormData(prev => ({ ...prev, proposalDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                helperText="When did you get engaged?"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="New Surname After Wedding"
                value={formData.newSurname}
                onChange={(e) => setFormData(prev => ({ ...prev, newSurname: e.target.value }))}
                helperText="If changing surname"
                placeholder="e.g., Smith, Johnson-Wilson"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Save Information'}
        </Button>
      </Box>
    </Box>
  );
}

export default WeddingExtraDetails;
